import React, { useEffect, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { AlertCircle, AlertTriangle, BarChart3, Building2, RefreshCw, Siren } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { Avatar, Kpi, Pill, RANGOS, duracion } from './controlUi';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const nombre = (c) => `${c.employee.nombre} ${c.employee.apellido}`.trim();

// Horas con minutos para totales grandes: "62 h 51 min".
const horas = (min) => {
    const m = Math.round(min || 0);
    if (m < 60) return `${m} min`;
    return `${Math.floor(m / 60).toLocaleString('es-CO')} h${m % 60 ? ` ${String(m % 60).padStart(2, '0')} min` : ''}`;
};

const Alertas = ({ c, className }) => (
    <div className={cn('flex flex-wrap gap-1', className)}>
        {c.forgottenExits > 0 && (
            <Pill tono="naranja" title="Marcaciones con más de 16 h entre llegada y salida">
                {c.forgottenExits} salida{c.forgottenExits > 1 ? 's' : ''} olvidada{c.forgottenExits > 1 ? 's' : ''}
            </Pill>
        )}
        {c.absentDays >= 3 && <Pill tono="rojo">{c.absentDays} inasistencias</Pill>}
        {c.lateDays > 0 && c.arrivals > 0 && c.lateDays / c.arrivals >= 0.5 && <Pill tono="ambar">Tarde la mitad o más</Pill>}
    </div>
);

// Los tres primeros en tonos de rojo, del más intenso al más suave.
const PODIO = [
    { icon: Siren, color: 'text-red-600', borde: 'border-red-500/60', fondo: 'bg-red-500/10', texto: '1.º lugar' },
    { icon: AlertTriangle, color: 'text-orange-500', borde: 'border-orange-500/50', fondo: 'bg-orange-500/10', texto: '2.º lugar' },
    { icon: AlertCircle, color: 'text-amber-500', borde: 'border-amber-400/60', fondo: 'bg-amber-500/10', texto: '3.º lugar' },
];

const TarjetaPodio = ({ c, onVer }) => {
    const p = PODIO[c.rank - 1];
    const Icono = p.icon;
    return (
        <button
            type="button"
            onClick={onVer}
            className={cn(
                'flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition hover:shadow-md',
                p.borde,
                p.fondo,
                c.rank === 1 && 'md:-translate-y-2',
            )}
        >
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Icono className={cn('h-5 w-5', p.color)} /> {p.texto}
            </div>
            <Avatar emp={c.employee} size="h-14 w-14" />
            <div>
                <p className="font-semibold leading-tight">{nombre(c)}</p>
                <p className="text-xs text-muted-foreground">{c.planta.nombre}</p>
            </div>
            <p className="text-3xl font-bold tabular-nums text-red-600 dark:text-red-400">{horas(c.lostMinutes)}</p>
            <p className="text-xs text-muted-foreground">
                {c.lateDays ? `${c.lateDays} de ${c.arrivals} llegadas tarde (${horas(c.lateMinutes)})` : 'Sin llegadas tarde'}
                {c.absentDays ? ` · ${c.absentDays} turno${c.absentDays > 1 ? 's' : ''} sin presentarse (${horas(c.absentMinutes)})` : ''}
            </p>
            <Alertas c={c} className="justify-center" />
        </button>
    );
};

const FilaPlanta = ({ c, onVer }) => (
    <button
        type="button"
        onClick={onVer}
        className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left transition hover:bg-muted/60"
    >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-xs font-semibold text-red-700 dark:text-red-300">
            {c.rank}
        </span>
        <Avatar emp={c.employee} size="h-7 w-7" />
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-tight">{nombre(c)}</p>
            <p className="text-xs text-muted-foreground">
                {c.lateDays ? `${c.lateDays} tarde (${horas(c.lateMinutes)})` : 'Sin llegadas tarde'}
                {c.absentDays ? ` · ${c.absentDays} sin presentarse` : ''}
            </p>
        </div>
        <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-red-600 dark:text-red-400">{horas(c.lostMinutes)}</span>
    </button>
);

const Incumplidos = ({ onVerPersona }) => {
    const { toast } = useToast();
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);

    const generar = async (desde = startDate, hasta = endDate) => {
        if (hasta < desde) {
            toast({ title: 'Rango no válido', description: 'La fecha fin es anterior a la de inicio.', variant: 'destructive' });
            return;
        }
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_URL}/shifts/control/highlights`, { params: { startDate: desde, endDate: hasta } });
            setResultado(data);
        } catch (error) {
            console.error('Error cargando incumplidos:', error);
            toast({
                title: 'Error',
                description: error?.response?.data?.message || 'No se pudo calcular el top de incumplidos.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        generar();
    }, []);

    const ver = (c) => onVerPersona(c.employee.id, resultado.startDate, resultado.endDate);
    const r = resultado;
    // Plantas con incumplimiento, de más a menos tiempo perdido.
    const plantas = r ? [...r.byCenter].filter((p) => p.lostMinutes > 0).sort((a, b) => b.lostMinutes - a.lostMinutes) : [];

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Desde</Label>
                            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-44" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Hasta</Label>
                            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-44" />
                        </div>
                        <Button onClick={() => generar()} disabled={loading}>
                            {loading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <BarChart3 className="mr-2 h-4 w-4" />}
                            {loading ? 'Calculando…' : 'Actualizar'}
                        </Button>
                        <div className="flex flex-wrap gap-2">
                            {RANGOS.map((rg) => (
                                <Button
                                    key={rg.label}
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => {
                                        const [a, b] = rg.calc();
                                        const desde = format(a, 'yyyy-MM-dd');
                                        const hasta = format(b, 'yyyy-MM-dd');
                                        setStartDate(desde);
                                        setEndDate(hasta);
                                        generar(desde, hasta);
                                    }}
                                >
                                    {rg.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {!r ? (
                <Card>
                    <CardContent className="flex h-48 items-center justify-center text-muted-foreground">
                        {loading ? 'Calculando el top de incumplidos…' : 'Sin datos.'}
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                        <Kpi
                            icon={AlertTriangle}
                            label="Tiempo perdido"
                            value={horas(r.summary.lostMinutes)}
                            sub="Minutos tarde + turnos sin presentarse"
                            tone="text-red-600 dark:text-red-400"
                        />
                        <Kpi label="Por llegadas tarde" value={horas(r.summary.lateMinutes)} sub={`Puntualidad general ${String(r.summary.punctualityRate ?? '—').replace('.', ',')} %`} />
                        <Kpi label="Turnos sin presentarse" value={r.summary.absentDays} sub="Sin incapacidad ni permiso registrado" tone={r.summary.absentDays ? 'text-red-600 dark:text-red-400' : undefined} />
                        <Kpi label="Personas con incumplimiento" value={`${r.summary.offenders} de ${r.summary.people}`} sub="Con al menos 3 turnos en el periodo" />
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold">Los que más incumplen en la empresa</h3>
                        <p className="text-xs text-muted-foreground">
                            Del {format(new Date(`${r.startDate}T12:00:00`), "d 'de' MMM yyyy", { locale: es })} al{' '}
                            {format(new Date(`${r.endDate}T12:00:00`), "d 'de' MMM yyyy", { locale: es })}. Ordenado por tiempo perdido. Haz clic
                            en una persona para ver su detalle día a día.
                        </p>
                    </div>

                    {r.offenders.length === 0 ? (
                        <Card>
                            <CardContent className="flex h-32 items-center justify-center text-muted-foreground">
                                Nadie tiene incumplimientos en el periodo.
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="mx-auto grid max-w-4xl grid-cols-1 items-end gap-3 pt-2 sm:grid-cols-3">
                            {/* Orden visual del podio: 2.º, 1.º, 3.º */}
                            {[1, 0, 2]
                                .map((i) => r.offenders[i])
                                .filter(Boolean)
                                .map((c) => (
                                    <TarjetaPodio key={c.employee.id} c={c} onVer={() => ver(c)} />
                                ))}
                        </div>
                    )}

                    {r.offenders.length > 3 && (
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Del 4.º al {r.offenders.length}.º</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {(
                                <div className="overflow-x-auto rounded-md border">
                                    <Table className="[&_td]:py-2">
                                        <TableHeader>
                                            <TableRow className="bg-muted/40 hover:bg-muted/40">
                                                <TableHead className="w-10">#</TableHead>
                                                <TableHead>Persona</TableHead>
                                                <TableHead>Planta</TableHead>
                                                <TableHead className="text-right">Tiempo perdido</TableHead>
                                                <TableHead className="text-right">Llegadas tarde</TableHead>
                                                <TableHead className="text-right">Minutos tarde</TableHead>
                                                <TableHead className="text-right">Sin presentarse</TableHead>
                                                <TableHead className="text-right">Puntualidad</TableHead>
                                                <TableHead className="text-right">Asistencia</TableHead>
                                                <TableHead>Alertas</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {r.offenders.slice(3).map((c) => (
                                                <TableRow key={c.employee.id} className="cursor-pointer" onClick={() => ver(c)}>
                                                    <TableCell>
                                                        <span
                                                            className={cn(
                                                                'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                                                                c.rank <= 3 ? 'bg-red-600 text-white' : 'bg-red-500/10 text-red-700 dark:text-red-300',
                                                            )}
                                                        >
                                                            {c.rank}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="min-w-[230px]">
                                                        <div className="flex items-center gap-3">
                                                            <Avatar emp={c.employee} size="h-8 w-8" />
                                                            <div>
                                                                <div className="font-medium leading-tight">{nombre(c)}</div>
                                                                <div className="text-xs text-muted-foreground">{c.employee.cedula || 'Sin cédula'}</div>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-sm">{c.planta.nombre}</TableCell>
                                                    <TableCell className="whitespace-nowrap text-right font-semibold tabular-nums text-red-600 dark:text-red-400">
                                                        {horas(c.lostMinutes)}
                                                    </TableCell>
                                                    <TableCell className="text-right tabular-nums">
                                                        {c.lateDays ? `${c.lateDays} de ${c.arrivals}` : '—'}
                                                    </TableCell>
                                                    <TableCell className="whitespace-nowrap text-right tabular-nums">{c.lateMinutes ? horas(c.lateMinutes) : '—'}</TableCell>
                                                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                                                        {c.absentDays ? (
                                                            <span title={`${horas(c.absentMinutes)} de turnos planificados`}>
                                                                {c.absentDays} · {horas(c.absentMinutes)}
                                                            </span>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right tabular-nums">
                                                        {c.punctualityRate == null ? '—' : `${String(c.punctualityRate).replace('.', ',')} %`}
                                                    </TableCell>
                                                    <TableCell className="text-right tabular-nums">{c.attendanceRate == null ? '—' : `${c.attendanceRate} %`}</TableCell>
                                                    <TableCell>
                                                        <Alertas c={c} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    )}

                    <div>
                        <h3 className="text-lg font-semibold">Incumplidos por planta</h3>
                        <p className="text-xs text-muted-foreground">Las plantas van ordenadas por tiempo perdido de su personal.</p>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {plantas.map((p) => (
                            <Card key={p.id}>
                                <CardHeader className="pb-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <CardTitle className="flex items-center gap-2 text-base">
                                                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" /> {p.nombre}
                                            </CardTitle>
                                            <CardDescription>
                                                {p.offenders} de {p.people} personas · {p.absentDays} turnos sin presentarse
                                            </CardDescription>
                                        </div>
                                        <div className="text-right">
                                            <p className="whitespace-nowrap text-lg font-semibold tabular-nums text-red-600 dark:text-red-400">{horas(p.lostMinutes)}</p>
                                            <p className="text-[11px] text-muted-foreground">perdidas</p>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-0.5 pt-0">
                                    {p.bottom.map((c) => (
                                        <FilaPlanta key={c.employee.id} c={c} onVer={() => ver(c)} />
                                    ))}
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    <p className="text-xs text-muted-foreground">
                        Tiempo perdido = minutos de llegada tarde + duración de los turnos planificados a los que no se presentó (sin
                        incapacidad ni permiso registrado). Entra quien tuvo al menos 3 turnos exigibles en el periodo. Llegar después
                        de la hora de inicio cuenta como tarde. Antes de llamar la atención, revisa el detalle: un turno «sin
                        presentarse» puede ser un cambio de turno que no se actualizó en la planificación.
                    </p>
                </>
            )}
        </div>
    );
};

export default Incumplidos;
